import { useCallback, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  rotation: number;
  rotationSpeed: number;
  life: number;
  maxLife: number;
  shape: 'square' | 'circle' | 'star';
}

const COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--accent))',
  'hsl(45, 100%, 60%)',   // Gold
  'hsl(280, 70%, 60%)',   // Purple
  'hsl(180, 70%, 50%)',   // Teal
  'hsl(350, 80%, 60%)',   // Rose
];

export const useConfetti = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const particlesRef = useRef<Particle[]>([]);

  const createCanvas = useCallback(() => {
    if (canvasRef.current) return canvasRef.current;
    
    const canvas = document.createElement('canvas');
    canvas.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      pointer-events: none;
      z-index: 9999;
    `;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    document.body.appendChild(canvas);
    canvasRef.current = canvas;
    return canvas;
  }, []);

  const removeCanvas = useCallback(() => {
    if (canvasRef.current) {
      canvasRef.current.remove();
      canvasRef.current = null;
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  }, []);

  const createParticles = useCallback((count: number, originX: number, originY: number, spread: number = 60): Particle[] => {
    const particles: Particle[] = [];
    const shapes: Particle['shape'][] = ['square', 'circle', 'star'];
    
    for (let i = 0; i < count; i++) {
      const angle = (Math.random() * spread - spread / 2) * (Math.PI / 180) - Math.PI / 2;
      const velocity = 8 + Math.random() * 12;
      
      particles.push({
        x: originX,
        y: originY,
        vx: Math.cos(angle) * velocity * (0.5 + Math.random()),
        vy: Math.sin(angle) * velocity,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        size: 4 + Math.random() * 8,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 15,
        life: 1,
        maxLife: 80 + Math.random() * 40,
        shape: shapes[Math.floor(Math.random() * shapes.length)],
      });
    }
    return particles;
  }, []);

  const drawParticle = useCallback((ctx: CanvasRenderingContext2D, p: Particle) => {
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate((p.rotation * Math.PI) / 180);
    ctx.globalAlpha = p.life;
    ctx.fillStyle = p.color;

    if (p.shape === 'square') {
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
    } else if (p.shape === 'circle') {
      ctx.beginPath();
      ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
      ctx.fill();
    } else if (p.shape === 'star') {
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const angle = (i * 4 * Math.PI) / 5 - Math.PI / 2;
        const r = i === 0 ? p.size / 2 : p.size / 2;
        if (i === 0) {
          ctx.moveTo(Math.cos(angle) * r, Math.sin(angle) * r);
        } else {
          ctx.lineTo(Math.cos(angle) * r, Math.sin(angle) * r);
        }
      }
      ctx.closePath();
      ctx.fill();
    }

    ctx.restore();
  }, []);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    particlesRef.current = particlesRef.current.filter(p => {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.25; // gravity
      p.vx *= 0.99; // air resistance
      p.rotation += p.rotationSpeed;
      p.life -= 1 / p.maxLife;

      if (p.life > 0) {
        drawParticle(ctx, p);
        return true;
      }
      return false;
    });

    if (particlesRef.current.length > 0) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      removeCanvas();
    }
  }, [drawParticle, removeCanvas]);

  const fire = useCallback((options?: { 
    particleCount?: number; 
    x?: number; 
    y?: number;
    spread?: number;
  }) => {
    const {
      particleCount = 80,
      x = window.innerWidth / 2,
      y = window.innerHeight / 3,
      spread = 120,
    } = options || {};

    createCanvas();
    
    const newParticles = createParticles(particleCount, x, y, spread);
    particlesRef.current = [...particlesRef.current, ...newParticles];

    if (!animationRef.current) {
      animate();
    }
  }, [createCanvas, createParticles, animate]);

  const burst = useCallback((options?: { x?: number; y?: number }) => {
    const x = options?.x ?? window.innerWidth / 2;
    const y = options?.y ?? window.innerHeight / 2;

    // Fire from multiple angles for a burst effect
    fire({ particleCount: 40, x, y, spread: 360 });
  }, [fire]);

  const celebration = useCallback(() => {
    // Multiple bursts from different positions
    const positions = [
      { x: window.innerWidth * 0.2, y: window.innerHeight * 0.3 },
      { x: window.innerWidth * 0.8, y: window.innerHeight * 0.3 },
      { x: window.innerWidth * 0.5, y: window.innerHeight * 0.2 },
    ];

    positions.forEach((pos, i) => {
      setTimeout(() => {
        fire({ particleCount: 60, x: pos.x, y: pos.y, spread: 100 });
      }, i * 150);
    });
  }, [fire]);

  return {
    fire,
    burst,
    celebration,
  };
};

export default useConfetti;
